import { Backdrop, CircularProgress } from "@mui/material";
import { Suspense, lazy } from "react";
import {
	Navigate,
	Routes as ReactRouterRoutes,
	Route,
	HashRouter as Router
} from "react-router-dom";

import { AuthenticatedRoute, ChangePassword, ForgotPassword, ResetPassword } from "@sal/portal";

import PermissionRoute from "components/PermissionRoute";

const PageLayout = lazy(() => import("./PageLayout"));
const CommunicationsPage = lazy(() => import("./pages/communications/CommunicationsPage"));
const PersonnelDetailPage = lazy(() => import("./pages/communications/PersonnelDetailPage"));
const FaqsPage = lazy(() => import("./pages/faqs/FaqsPage"));
const FaqDetailPage = lazy(() => import("./pages/faqs/FaqDetailPage"));
const LoginPage = lazy(() => import("./pages/login/LoginPage"));
const NoAccessPage = lazy(() => import("./pages/noAccess/NoAccessPage"));
const SalCoreLoginPage = lazy(() => import("./pages/login/SalCoreLoginPage"));
const NotFoundPage = lazy(() => import("./pages/notFound/NotFoundPage"));

/**
 * Admin2's routing table.
 *
 * Route-level authorisation uses the `Routes` security context, split across two components: the
 * outer bare `<AuthenticatedRoute />` requires a signed-in session, and `<PermissionRoute />`
 * requires a named permission within `Routes` (e.g. `Routes` -> `Home`). Those permission names are
 * case-sensitive dictionary keys in the SALCore permissions payload — see the JSON serialiser
 * comment in `Program.cs` before changing anything about how they are cased.
 *
 * `PermissionRoute` exists only to change where a permission failure lands: `/NoAccess` rather than
 * `AuthenticatedRoute`'s `/NotFound`. Sign-in auto-provisions unknown Entra users with zero user
 * groups, so "signed in and allowed nothing" is now a normal state that deserves an explanation
 * rather than a 404.
 *
 * Sign-in is Microsoft Entra, via SAL.Login's `/SalLogin/Auth/...` endpoints. `/Login` is where an
 * unauthenticated visitor ends up: `AuthenticatedRoute` redirects there (with `returnUrl`) whenever
 * `isLoggedIn` is false, and SAL.Portal's `AuthController` redirects there with a `reason` code
 * when a login fails — so it is declared OUTSIDE `AuthenticatedRoute`, and outside `PageLayout`,
 * like the other unauthenticated pages. The local impersonation mock pair in `Program.cs` is still
 * available as an alternative for development.
 *
 * `/SalCoreLogin` is the one exception to "sign-in is Entra": an unlinked SALCore
 * username/password form, enabled in all environments. See the comment on the route itself.
 */
export default function Routes() {
	return (
		<Router>
			<Suspense
				fallback={
					<Backdrop invisible open>
						<CircularProgress />
					</Backdrop>
				}
			>
				<ReactRouterRoutes>
					<Route element={<PageLayout />}>
						<Route element={<AuthenticatedRoute />}>
							{/* `/` is the landing route — it is where `SalLoginOptions:DefaultRedirectUrl`
							    (`/#/`) drops every successful sign-in, where the top-bar logo goes, and
							    where NotFoundPage's primary button goes. There is no Home content YET, so
							    it forwards to the only feature area that exists.

							    `replace` is load-bearing: without it `/` stays on the history stack, and
							    Back from Communications returns here only to be redirected forward again
							    — a trap the user cannot escape with the Back button.

							    Still gated on `Routes`/`Home`, deliberately: the redirect is a convenience
							    for people who have access, not a way around the gate, so a user without it
							    lands on `/NoAccess` exactly as before. Note this now assumes `Routes`/`Home`
							    and `Communications`/`Read` travel together — someone holding the first but
							    not the second gets bounced through here to `/NoAccess` rather than seeing a
							    harmless landing page. `Orca Admin2 Administrators` (the only group) grants
							    both, so no conditional logic exists for a state nobody is in.

							    The placeholder page is still here: `pages/home/HomePage.tsx` plus its test.
							    To restore it, re-add `const HomePage = lazy(() => import("./pages/home/HomePage"));`
							    above and swap this `<Navigate />` back for `<HomePage />`. */}
							<Route
								path="/"
								element={
									<PermissionRoute securityContext="Routes" securityContextPermission="Home">
										<Navigate to="/Communications" replace />
									</PermissionRoute>
								}
							/>
							{/* Communications Management is gated on its own security context, NOT on
							    `Routes`/`Home` — holding the landing page says nothing about whether a
							    user may see customer contact details. The same pair gates the API
							    action (`[AuthorisedSession("Communications", "Read")]`) and the top-bar
							    nav item, so the three cannot disagree. */}
							<Route
								path="/Communications"
								element={
									<PermissionRoute
										securityContext="Communications"
										securityContextPermission="Read"
									>
										<CommunicationsPage />
									</PermissionRoute>
								}
							/>
							{/* Gated on `Read`, like the listing. Saving additionally needs
							    `Communications`/`Write`, which the page checks itself — it renders the
							    matrix read-only rather than offering a Save that would 403. */}
							<Route
								path="/Communications/:contactId"
								element={
									<PermissionRoute
										securityContext="Communications"
										securityContextPermission="Read"
									>
										<PersonnelDetailPage />
									</PermissionRoute>
								}
							/>
							{/* FAQ management, gated on its own security context — like Communications,
							    NOT on `Routes`/`Home`. Both the listing and the create/edit page need only
							    `Faq`/`Read` to be *reached*; Save/Delete/New additionally need `Faq`/`Write`,
							    which the pages themselves check (mirroring `PersonnelDetailPage`'s `canWrite`
							    pattern) so a viewer never sees a control that is certain to 403. */}
							<Route
								path="/Faqs"
								element={
									<PermissionRoute securityContext="Faq" securityContextPermission="Read">
										<FaqsPage />
									</PermissionRoute>
								}
							/>
							{/* One dynamic route serves both `/Faqs/new` (create) and `/Faqs/:faqId` (edit):
							    `:faqId` happily captures the literal segment "new" as its value, and
							    `FaqDetailPage` checks the parameter against that literal string *before*
							    `Number.parseInt`-ing it — see `code/faqNavigation.ts` and the page's own
							    comment. A second, static `path="/Faqs/new"` route is deliberately NOT
							    declared alongside this one: React Router ranks a static segment above a
							    dynamic one, so it would win the match and `useParams()` would return no
							    `faqId` at all for it — silently turning every "New FAQ" click into the
							    not-found panel instead of the create form. */}
							<Route
								path="/Faqs/:faqId"
								element={
									<PermissionRoute securityContext="Faq" securityContextPermission="Read">
										<FaqDetailPage />
									</PermissionRoute>
								}
							/>
							{/* Authenticated, but gated on nothing — it is where a user with no
							    permissions at all is sent. */}
							<Route path="/NoAccess" element={<NoAccessPage />} />
						</Route>
					</Route>
					<Route path="/Login" element={<LoginPage />} />
					{/* SALCore username/password login — a real, working second way in, live in every
					    environment, not dead code. It authenticates against SALCore contacts via
					    SAL.Portal's `[AllowAnonymous]` `/PortalFramework/Account/Login`, bypassing Entra
					    entirely, so treat it as a credential back-door on an otherwise Entra-only portal.
					    It is unlinked — nothing in the UI points at it — and reachable only by typing
					    `/#/SalCoreLogin`.

					    Deliberately declared OUTSIDE `AuthenticatedRoute`/`PermissionRoute`: a login page
					    that required a session would be useless. Deleting this one route is the off
					    switch, and is all that is needed to disable it. */}
					<Route path="/SalCoreLogin" element={<SalCoreLoginPage />} />
					{/* PORTAL FRAMEWORK PAGES*/}
					<Route path="/ForgotPassword" element={<ForgotPassword />} />
					<Route path="/ChangePassword" element={<ChangePassword />} />
					<Route path="/ResetPassword/:passwordResetToken" element={<ResetPassword />} />
					<Route path="/NotFound" element={<NotFoundPage />} />
					<Route path="*" element={<NotFoundPage />} />
					{/* /PORTAL FRAMEWORK PAGES*/}
				</ReactRouterRoutes>
			</Suspense>
		</Router>
	);
}
